import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import * as kendoDrawing from '@progress/kendo-drawing';
import { PDFOptions } from '@progress/kendo-drawing/dist/npm/pdf';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { DestroyBase } from '../../../../components/common/ng-destroy-base';
import { AppLoadingIndicatorService } from '../../../../services/app-loading-indicator.service';
import { AppServiceHelperService } from '../../../../services/app-service-helper.service';
import { EventBusService } from '../../../../services/event-bus.service';
import { FileDownloadService } from '../../../../services/file-download-service.service';
import { GuidService } from '../../../project-valuation/services/guid.service';
import { ReportDesignerDocument } from '../../models/report-designer-document';
import { ReportDesignerPage } from '../../models/report-designer-page';
import { ReportDesignerPrintSettings } from '../../models/report-designer-print-settings';
import { ReportDesignerHostComponent } from '../../report-designer-host/report-designer-host.component';
import { ReportDesignerStorageService } from '../../services/report-designer-storage.service';
import { ReportDesignerWidgetConfig } from '../../models/report-designer-widget-config';
import { PrintServiceResolver } from '../../services/print/print-service-resolver';
import { PrintService } from '../../services/print/print.service';
import { ReportDesignerPageComponent } from '../report-designer-page/report-designer-page.component';
import { PrintPageParameters } from './models/print-page-parameters';
import { WidgetDynamicBaseComponent } from '../../widgets/widgetDynamicBase';
import { ReportDesignerWidgetGrowthInfo } from '../../models/report-designer-widget-growth-info';
import { ObjectUtil } from '../../../../components/common/object-util/object-util';
import { BehaviorSubject } from 'rxjs';
import { WidgetService } from '../../widgets/widget.service';

/**
 * RD document component
 */
@Component({
  selector: 'report-designer-document',
  templateUrl: './report-designer-document.component.html',
  styleUrls: ['./report-designer-document.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportDesignerDocumentComponent extends DestroyBase implements OnInit {
  /**
   * Host component reference
   */
  @Input() public main: ReportDesignerHostComponent;

  /**
   * ViewChild reference to the document element
   */
  @ViewChild("documentRef") documentRef: ElementRef;

  /**
   * Document load start time
   */
  private documentStart: Date = null;

  /**
   * Loading indicator
   */
  public loadingDocument = false;

  /**
   * RD document
   */
  public data: ReportDesignerDocument = null;

  /**
   * Reference to Math.xyz functions in HTML
   */
  public Math = Math;

  /**
   * Print service
   */
  private printService: PrintService;

  /**
   * trackby helper for *ngFor
   * @param _index Index
   * @param page Field name
   * @returns 
   */
  public trackByPageId(_index: number, page: ReportDesignerPage): string {
    return page.options.id;
  }

  /**
   * Constructor
   * @param appLoadingIndicator
   * @param rdStorage
   * @param guidService
   * @param eventBus
   * @param http
   * @param appServiceHelper
   * @param fileDownloadService
   * @param printServiceResolver
   */
  constructor(private appLoadingIndicator: AppLoadingIndicatorService,
    private rdStorage: ReportDesignerStorageService,
    private guidService: GuidService,
    private eventBus: EventBusService,
    private http: HttpClient,
    private appServiceHelper: AppServiceHelperService,
    private fileDownloadService: FileDownloadService,
    private printServiceResolver: PrintServiceResolver,
    private ref: ChangeDetectorRef,
    private widgetService: WidgetService) {
    super();
  }

  /**
   * Initializes the component
   */
  public ngOnInit(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('deletePage', (index) => {
      //remove page from pages array
      this.data.pages.splice(index, 1);
      this.syncToPageList();
      this.eventBus.broadcastEvent("pageDeleted", index);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('hideOrShowPage', (pageIndex: number) => {
      this.handleHideOrShowPage(pageIndex);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('AngWidgets.getWidgetInfo.question', (widgetGuid: string) => {
      this.handleWidgetInfoQuestion(widgetGuid);
    }));

    this.subscribeToSpaceRequests();

    this.main.setActiveDocument(this);
    if (this.data) {
      this.syncToPageList();
    }
  }

  /**
   * Handle showing or hiding a page
   * @param pageIndex
   */
  private handleHideOrShowPage(pageIndex: number): void {
    const page = this.data.pages[pageIndex];
    page.options.isHidden = !page.options.isHidden;

    const currentActivePage = this.main.activePage;

    //visible pages of same type (including one that is being hidden now)
    const pages = this.data.pages.filter(p => (p.options.isPortrait == page.options.isPortrait && !p.options.isHidden) || p === page);

    if (page === currentActivePage) {
      if (page.options.isHidden) {
        this.handleHideCurrentPage(page);
      }
    } else if (!page.options.isHidden) {
      if (pages.length === 1) {
        this.eventBus.broadcastEvent("pageNavigation:activatePage", null);
      } else {
        //keep current page active (and in view)
        this.main.setActivePage(currentActivePage, true);
      }
    }
  }

  /**
   * Handle hiding the current page
   * @param page
   */
  private handleHideCurrentPage(page: ReportDesignerPage) {
    //visible pages of same type (including one that is being hidden now)
    const pages = this.data.pages.filter(p => (p.options.isPortrait == page.options.isPortrait && !p.options.isHidden) || p === page);
    const hideShowPageIndex = pages.indexOf(page);

    let selectNextPage = null;

    //try select next
    for (let idx = hideShowPageIndex + 1; idx < pages.length; idx++) {
      const page = pages[idx];
      if (!page.options.isHidden) {
        selectNextPage = page;
        break;
      }
    }

    if (!selectNextPage) {
      //try select previous
      for (let idx = hideShowPageIndex - 1; idx >= 0; idx--) {
        const page = pages[idx];
        if (!page.options.isHidden) {
          selectNextPage = page;
          break;
        }
      }
    }

    this.main.setActivePage(selectNextPage, true);
  }

  /**
   * Synchronizes to the page list event
   */
  public syncToPageList(): void {
    this.eventBus.broadcastEvent("syncToPageList", this.data.pages);
  }

  /**
   * Handles the widget info question event
   * @param widgetGuid
   */
  private handleWidgetInfoQuestion(widgetGuid: string): void {
    const response = {
      widgetConfig: null,
      page: null
    };

    if (this.data?.pages?.length) {
      const widgetFindReult = this.findWidget(widgetGuid);

      if (widgetFindReult) {
        response.widgetConfig = widgetFindReult.widget;
        const ret = {
          id: widgetFindReult.page.options.id,
          callback: (pageVM) => {
            response.page = pageVM;
            this.eventBus.broadcastEvent('AngWidgets.getWidgetInfo.answer', response);
          }
        }

        this.eventBus.broadcastEvent('getPageViewModelByPageId', ret);
      }
    } else {
      this.eventBus.broadcastEvent('AngWidgets.getWidgetInfo.answer', response);
    }
  }

  /**
   * Finds a widget
   * @param widgetGuid
   * @returns
   */
  private findWidget(widgetGuid: string): { page: ReportDesignerPage, widget: ReportDesignerWidgetConfig } {
    const pages = this.data.pages;
    for (const page of pages) {
      if (page.widgets?.length) {
        const widgets = page.widgets;
        for (const widget of widgets) {
          if (widget.widgetSettings.id === widgetGuid) {
            return {
              page: page, widget: widget
            };
          }
        }
      }
    }
  }

  /**
   * Clears the document
   */
  public clear(): void {
    this.data = null;
    this.main.setActiveDocument(this);
    this.main.setActivePage(null, false);
    this.main.setActiveWidget(null);
    this.eventBus.broadcastEvent('widgetsChanged');
  }

  /**
   * Adds an page to current document at position pageIndex
   * @param page  Page to be added
   * @param pageIndex Add to pageIndex position
   * @param setActive If true, set page active
   */
  public addPage(page: ReportDesignerPage, pageIndex: number, setActive: boolean): ReportDesignerPage {
    const pages = this.data.pages;
    pageIndex = (typeof (pageIndex) === "undefined" ? pages.length : pageIndex);
    setActive = typeof (setActive) === "undefined" ? true : setActive;

    if (typeof (page.options.isPortrait) == 'undefined') {
      page.options.isPortrait = page.options.name.indexOf("-H") === -1;
    }

    if (!page.options.id) {
      page.options.id = this.guidService.generate();
    }

    pages.splice(pageIndex, 0, page);

    if (this.rdStorage.getAppWidgetHeaderMode()) {
      page.options.draggable.enabled = true;
      page.options.resizable.enabled = true;
    }

    this.syncToPageList();

    if (setActive) {
      this.eventBus.broadcastEvent('pageAdded', pageIndex);
      this.main.setActivePage(page, true);
    }

    return page;
  }

  /**
   * Removes virtual pages
   */
  public removeVirtualPages(): void {
    const pages = this.data.pages || [];
    for (let idx = pages.length - 1; idx >= 0; idx--) {
      if (pages[idx] && !pages[idx].id) {
        pages[idx].options.id = this.guidService.generate();
      }

      if (pages[idx].isVirtualPage || pages[idx] === null) {
        pages.splice(idx, 1);
      }
    }
  }

  /**
   * Loads document that should be displayed
   * @param doc Document definition
   */
  public loadDocument(doc: ReportDesignerDocument): void {
    this.loadingDocument = true;

    this.clear();

    this.data = doc;

    this.removeVirtualPages()//remove virtual pages (used by f.e. HRentRollMonth)

    for (const page of this.data.pages) {
      page.options.margin = 0;
      page.options.outerMarginTop = 0;
      page.options.outerMarginRight = 0;
      page.options.outerMarginBottom = 0;
      page.options.outerMarginLeft = 5;
      page.options.outerMargin = true;
      page.options.draggable.enabled = false;
      page.options.resizable.enabled = false;
      page.options.isOnDisplay = true; //By default, all pages are shown.

      page.dataLoaded = false;
      //reset dataLoaded on page and widgets (it is saved to database in templateJSON)
      for (const widget of page.widgets) {
        widget.dataLoaded = false;
      }
    }

    this.main.setActiveDocument(this);
    if (this.data.pages.length) {
      this.main.setActivePage(this.data.pages[0], false);
    }
    this.syncToPageList();

    this.eventBus.broadcastEvent('widgetsChanged');
    this.documentStart = new Date();

    //initialize print service (document has to be loaded when init is called)
    this.printService = this.printServiceResolver.get();
    this.printService.init(this.main);
  }

  /**
   * Page data loaded callback
   * @param page
   */
  public pageDataLoaded(page: ReportDesignerPageComponent): void {
    this.printService.pageDataLoaded(page);

    let documentLoaded = true;
    for (const page of this.data.pages) {
      if (page.widgets.length === 0) {
        page.dataLoaded = true;
      }

      documentLoaded = documentLoaded && page.dataLoaded;
    }

    if (documentLoaded) {
      this.loadingDocument = false;
      if (this.documentStart) {
        console.log("DOCUMENT LOADED! " + (new Date().getTime() - this.documentStart.getTime()));
      }
      else {
        console.warn('Document loading start not set!');
      }

      if (this.hadChangeSizeRequests) {
        this.hadChangeSizeRequests = false;
        this.eventBus.broadcastEvent('openConfirmDialog', {
          success: () => {
            //Nothing
          }
        }, 'widgetLayoutsChanged');
      }
      this.printService.documentDataLoaded();

    }
  }

  /**
   * Page data saved callback
   */
  public pageDataSaved(): void {
    let documentSaved = true;
    for (const page of this.data.pages) {
      if (page.widgets.length === 0) {
        page.dataSaved = true;
      }

      documentSaved = documentSaved && page.dataSaved;
    }

    if (documentSaved) {
      this.eventBus.broadcastEvent("report.designer.widgets.saved");
    }
  }

  /**
   * Exports the displayed document to PDF
   * @param settings Settings
   */
  public exportPDF(settings: ReportDesignerPrintSettings): void {
    if (this.data?.pages?.length) {

      this.preparePagesForPdf(settings);
      const lastPageIdx = this.data.pages.length - 1;
      const guid = this.guidService.generate();

      this.main.raisePrintEvent("pdf-export-pages_" + this.data.pages.length);
      this.main.raisePrintEvent("pdf-export-pages");

      if (settings.hidePageNumbers) {
        this.main.addDocumentClass("reportPageNumbersHidden");
      }

      if (!settings.includeHeader) {
        this.main.addDocumentClass("reportHeaderHidden");
      }

      if (!settings.includeFooter) {
        this.main.addDocumentClass("reportFooterHidden");
      }

      this.main.removeDocumentClass("ReportOverviewMode");

      const shouldExportHtmlSnapshot = settings.printMethod == 6;//HTML

      setTimeout(() => {
        if (shouldExportHtmlSnapshot) {
          this.main.setPrintProgress('publish.htmlSnapshot', 0, 1, false);
          this.exportStaticHtml().then(() => {
            this.pdfExportEnded();
          });
        } else {
          const printParameters: PrintPageParameters = {
            guid,
            settings,
            pageSizeId: this.data.pageSizeId,
            lastPageIdx
          };

          this.printPage(0, 1, printParameters);
        }
      }, 500);//Necessary - wait until the classes etc are applied
    }
  }

  /**
   * Prepare pages for printing to PDF
   * @param settings
   */
  private preparePagesForPdf(settings: ReportDesignerPrintSettings): void {
    for (const page of this.data.pages) {
      page.options.isOnDisplayBeforePrint = page.options.isOnDisplay;
      page.options.isHiddenBeforePrint = page.options.isHidden;

      page.options.isOnDisplay = true;

      if (page.options.isCoverPage) {
        if (!settings.hideCoverPage) {
          page.options.isHidden = false;
          page.options.isOnDisplay = true;
        }
        else {
          page.options.isHidden = true;
          page.options.isOnDisplay = false;
        }
      }
      if (page.options.isBackCoverPage) {
        if (!settings.hideBackCoverPage) {
          page.options.isHidden = false;
          page.options.isOnDisplay = true;
        }
        else {
          page.options.isHidden = true;
          page.options.isOnDisplay = false;
        }
      }
    }
  }

  /**
   * Converts images to data-url
   * @param {any} source
   * @param {any} element
   */
  private applyDataUrl(element: HTMLImageElement): void {
    try {
      // Create canvas
      const canvas: HTMLCanvasElement = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      // Set width and height
      canvas.width = element.naturalWidth;
      canvas.height = element.naturalHeight;
      // Draw the image
      ctx.drawImage(element, 0, 0);
      const dataUrl = canvas.toDataURL('image/png');
      element.src = dataUrl;
    } catch (e) {
      //Ignore
    }
  }

  /**
   * Applies the computed styles to elements, so no CSS needs to be loaded and HTMLS are immutable
   * @param {any} source
   * @param {any} element
   */
  private applyComputedStyle(source: HTMLElement, element: HTMLElement): void {
    const css: CSSStyleDeclaration = window.getComputedStyle(source, null);

    for (const key of Object.keys(css)) {
      if (key && typeof key !== "function") {
        const value = css.getPropertyValue(key);
        if (value) {
          try {
            element.style.setProperty(key, value);
          } catch (e) {
            //Ignore
          }
        }
      }
    }

    if (element instanceof HTMLImageElement) {
      this.applyDataUrl(element);
    }
  }

  /**
   * Builds the HTML string for a given root element and all child elements. Images and CSS are inlined.
   * @param {any} root
   */
  private buildHtml(root: HTMLElement): string {
    let htmlString = "";
    const temp: HTMLElement = root.cloneNode(true) as HTMLElement;

    //if text
    if (temp.nodeType == 3) {
      return temp.nodeValue;
    }

    //normal node with children
    if (temp.nodeType == 1) {
      if (temp.nodeType == 1 && root.childNodes && temp.childNodes.length > 0) {
        if (root.nodeName === 'WIDGET-BOX-HEADER' || root.classList.contains('widget-locker-overlay-horizontal') || root.classList.contains('widget-locker-overlay-vertical')) {
          return htmlString;
        }

        for (let i = 0; i < temp.childNodes.length; ++i) {
          htmlString += this.buildHtml(root.childNodes[i] as HTMLElement);
        }
        temp.innerHTML = htmlString;
        this.applyComputedStyle(root, temp);
        htmlString = temp.outerHTML;
      } else {
        //normal node, no children
        this.applyComputedStyle(root, temp);
        htmlString = temp.outerHTML;
      }
    }
    return htmlString;
  }

  /**
   * Exports an HTML snapshot
   */
  private exportStaticHtml(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (this.rdStorage.getReport()) {
        const root: HTMLElement = document.getElementById('report-designer-document');
        let html = this.buildHtml(root);

        const docTypeString = new XMLSerializer().serializeToString(document.doctype);
        html = docTypeString + html;

        const data = {
          reportId: this.rdStorage.getReport(),
          pdfExportId: this.main.PdfExportId,
          fileName: "Report.html",
          baseUrl: '',
          html: html,
          widgetsHtmls: []
        };

        const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportprint/htmlSnapshot');
        this.http.post(url, data).pipe(take(1)).subscribe({
          next: (response: any) => {
            resolve(response);
          },
          error: () => {
            resolve(false);
          }
        });
      } else {
        resolve(true);
      }
    });
  }

  /**
   *
   * Processes the next page
   * @param pageIdx
   * @param counter
   * @param printParameters
   */
  private processNextPage(pageIdx: number, counter: number, printParameters: PrintPageParameters): void {
    setTimeout(() => {
      if (pageIdx === printParameters.lastPageIdx) {
        this.pdfCreated(printParameters.guid, printParameters.settings); //This was the last page anyways -> Merge
      } else {
        this.printPage(pageIdx + 1, counter, printParameters); //Print the next page
      }
    });
  }

  /**
   *
   * Prints the page
   * @param pageIdx
   * @param counter
   * @param printParameters
   * @returns
   */
  private printPage(pageIdx: number, counter: number, printParameters: PrintPageParameters): void {
    const visiblePages = this.getVisiblePagesForPrint(printParameters.settings);

    this.main.setPrintProgress('publish.page', counter, visiblePages.length, false);

    const page = this.data.pages[pageIdx];

    const skipPage = page.options.isHidden;
    if (skipPage) {
      //This page is hidden - go to the next page!
      this.processNextPage(pageIdx, counter, printParameters);
      return; //Don't go any further
    }
    else {
      ++counter;
    }

    if (this.rdStorage.getAppPrintMode()) {
      this.main.raisePrintEvent("pdf-export-print-page_" + (pageIdx + 1));
    }

    const pageEl: HTMLElement = document.getElementById('pdf-page-' + pageIdx);

    kendoDrawing.pdf.defineFont({
      "Arial": "assets/fonts/arial.ttf", // this is a URL
      "Arial|Bold": "assets/fonts/arialbd.ttf",
      "Arial|Bold|Italic": "assets/fonts/arialbi.ttf",
      "Arial|Italic": "assets/fonts/ariali.ttf"
    });

    kendoDrawing.drawDOM(pageEl).then((drawDOMresult) => {
      this.exportPdf(drawDOMresult, pageIdx, counter, printParameters);
    });
  }

  /**
   * Exports the DOM result to PDF
   * @param drawDOMresult
   * @param pageIdx
   * @param counter
   * @param printParameters
   */
  private exportPdf(drawDOMresult: kendoDrawing.Group, pageIdx: number, counter: number, printParameters: PrintPageParameters): void {
    const options: PDFOptions = {
      multiPage: true
    };

    kendoDrawing.exportPDF(drawDOMresult, options).then((pageBase64) => {
      this.sendPagePdfToServer(pageBase64, pageIdx, counter, printParameters);
    });
  }

  /**
   * Sends the page PDF to the server
   * @param pageBase64
   * @param pageIdx
   * @param counter
   * @param printParameters
   */
  private sendPagePdfToServer(pageBase64: string, pageIdx: number, counter: number, printParameters: PrintPageParameters): void {
    pageBase64 = String(pageBase64).replace('data:application/pdf;base64,', '');
    //upload page to server
    const data = {
      DocumentBase64: pageBase64,
      PageNo: pageIdx,
      PageSizeId: printParameters.pageSizeId,
      PrintGuid: printParameters.guid,
      PdfExportId: this.main.PdfExportId || null
    };

    const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportprint/pdfupload');
    const headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json; charset=utf-8');

    this.http.post(url, data, { headers: headers }).pipe(take(1)).subscribe({
      next: () => {
        this.processNextPage(pageIdx, counter, printParameters);
      },
      error: () => {
        this.handlePrintError(pageIdx, counter, printParameters);
      }
    });
  }

  /**
   *
   * Handle print errors
   * @param pageIdx
   * @param counter
   * @param printParameters
   */
  private handlePrintError(pageIdx: number, counter: number, printParameters: PrintPageParameters): void {
    if (pageIdx === printParameters.lastPageIdx) {
      //Restore the pages' visibility
      for (const page of this.data.pages) {
        page.options.isOnDisplay = page.options.isOnDisplayBeforePrint;
        page.options.isHidden = page.options.isHiddenBeforePrint;
        delete page.options.isOnDisplayBeforePrint;
        delete page.options.isHiddenBeforePrint;
      }

      this.pdfCreated(printParameters.guid, printParameters.settings); //This was the last page anyways -> Merge
    } else {
      this.printPage(pageIdx + 1, counter, printParameters);
    }
  }

  /**
   * Gets the visible pages for print
   * @param settings
   * @returns
   */
  private getVisiblePagesForPrint(settings: ReportDesignerPrintSettings): ReportDesignerPage[] {
    return this.data.pages.filter((p) => {
      let shouldSkipPage = p.options.isHidden;
      if (p.options.isCoverPage) {
        //Skip the cover page if the checkbox isn't checked
        shouldSkipPage = !settings.coverPage;
      }
      if (p.options.isBackCoverPage) {
        //Skip the back cover page if the checkbox isn't checked
        shouldSkipPage = !settings.backCoverPage;
      }
      return !shouldSkipPage;
    });
  }

  /**
   * Called when all page PDFs are created
   * @param guid Print GUID
   * @param settings Print settings
   */
  private pdfCreated(guid: string, settings: ReportDesignerPrintSettings): void {
    //Restore the pages' visibility
    for (const page of this.data.pages) {
      page.options.isOnDisplay = page.options.isOnDisplayBeforePrint;
      page.options.isHidden = page.options.isHiddenBeforePrint;
      delete page.options.isOnDisplayBeforePrint;
      delete page.options.isHiddenBeforePrint;
    }

    //merge files
    const data = {
      ReportId: this.rdStorage.getReport(),
      PrintGuid: guid,
      KeepFiles: settings.serverSidePrint,
      settings: settings,
      PdfExportId: null
    };

    if (this.rdStorage.getAppPrintMode()) {
      this.main.raisePrintEvent("pdf-export-merging");
    }

    this.main.setPrintProgress('publish.merge', 1, 0, false);

    if (!settings.serverSidePrint) {
      const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportprint/merge');

      //if started by user (UI) => download file
      this.fileDownloadService.initiateDownloadPost(url, data).then(() => {
        this.pdfExportEnded();
      });
    } else {
      //in publish scenario (puppeteer), just merge (and do other server side stuff)
      data.PdfExportId = this.main.PdfExportId
      const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportprint/merge/publish');

      this.http.post(url, data).pipe(take(1)).subscribe(() => {
        this.pdfExportEnded();
      });
    }
  }

  /**
   * Called once the PDF export ended
   */
  private pdfExportEnded(): void {
    this.main.removeDocumentClass("print-in-progress");
    this.main.removeDocumentClass("reportPageNumbersHidden");
    this.main.removeDocumentClass("reportHeaderHidden");
    this.main.removeDocumentClass("reportFooterHidden");

    this.main.pdfExportFinished();
    this.main.setPrintProgress('publish.done', 0, 0, true);

    this.appLoadingIndicator.hide();
  }

  /**
   * Deletes / removes a widget
   * @param widgetId The widgetIs ID
   */
  public deleteWidget(widgetId: string): void {
    if (this.data?.pages) {
      for (const page of this.data.pages) {
        if (page.widgets) {
          for (let j = 0; j < page.widgets.length; ++j) {
            const widget = page.widgets[j];
            if (widget.widgetSettings.id == widgetId) {
              page.widgets.splice(j, 1);
              this.eventBus.broadcastEvent('widgetRemoved', widgetId);
              break;
            }
          }
        }
      }
    }
    this.eventBus.broadcastEvent('widgetsChanged');
  }

  /**
   * Moves the page to new position
   * @param fromIndex The page index
   * @param toIndex   The page index where to move page to
   */
  public movePage(fromIndex: number, toIndex: number) {
    if (fromIndex !== toIndex) {
      const pages = this.data.pages;
      const movingPage = pages.splice(fromIndex, 1)[0];
      pages.splice(toIndex, 0, movingPage);
      this.syncToPageList();
    }
  }

  /**
   * Scrolls to a page
   * @param page The page
   */
  public scrollToPage(page: ReportDesignerPage): void {
    //wait for redraw
    setTimeout(() => {
      const pagesCollection = document.getElementsByClassName(`pdf-page-${page.options.id}`);
      if (pagesCollection?.length && pagesCollection[0] instanceof HTMLElement) {
        const pageEl = pagesCollection[0];

        const isFirstPage = this.data.pages.filter(p => !p.options.isHidden).indexOf(page) === 0;

        const scrollToY = isFirstPage ? 0 : (pageEl.offsetTop - 35);//page has some top margin, so adjust a little
        const htmlElement = this.documentRef.nativeElement as HTMLElement;
        htmlElement.scrollTo(0, scrollToY);
        htmlElement.scrollLeft = 0;
      }
    });
  }

  /**
   * Scrolls to a widget
   * @param widgetPage The widget's page
   * @param widgetGuid The wdget's GUID
   */
  public scrollToWidget(widgetPage: ReportDesignerPage, widgetGuid: string): void {
    const pagesCollection = document.getElementsByClassName(`pdf-page-${widgetPage.options.id}`);
    if (pagesCollection?.length && pagesCollection[0] instanceof HTMLElement) {
      const pageEl = pagesCollection[0];

      const angWidgetEl = document.getElementsByClassName(`widget_id_${widgetGuid}`);
      if (angWidgetEl?.length && angWidgetEl[0] instanceof HTMLElement) {
        const widgetEl = angWidgetEl[0];

        const isFirstPage = this.data.pages.filter(p => !p.options.isHidden).indexOf(widgetPage) === 0;

        let scrollToY = isFirstPage ? 0 : (pageEl.offsetTop - 35);//page has some top margin, so adjust a little
        if (widgetEl.offsetParent instanceof HTMLElement) {
          const offsetParent = widgetEl.offsetParent;
          const widgetOnPageY = offsetParent.offsetTop;

          scrollToY += widgetOnPageY;//adjust for widget position
          const htmlElement: HTMLElement = this.documentRef.nativeElement;
          htmlElement.scrollTo(0, scrollToY);
          htmlElement.scrollLeft = 0;
        }
      }
    }
  }

  public spaceRequests$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  public requestMoreSpace(page: ReportDesignerPageComponent, widget: WidgetDynamicBaseComponent, growthInfo: ReportDesignerWidgetGrowthInfo) {
    const currentRequest = this.spaceRequests$.value;
    currentRequest.push({ page, widget, growthInfo });
    this.spaceRequests$.next(currentRequest);
  }

  private hadChangeSizeRequests = false;

  private subscribeToSpaceRequests() {
    this.subscriptions.add(this.spaceRequests$
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((requests: any[]) => {
        setTimeout(() => {
          if (requests.length) {
            this.hadChangeSizeRequests = true;
            const request = requests[0];
            this.handleSpaceRequest(request.page, request.widget, request.growthInfo);

            this.spaceRequests$.next([]);

            const pages = ObjectUtil.deepCopy(this.data.pages);
            this.data.pages = [];

            request.widget.check();
            this.ref.markForCheck();
            this.ref.detectChanges();
            setTimeout(() => {
              //Reload, so further changes can be consumed. Shouldn't be used in print
              this.data.pages = pages;
              request.widget.check();
              this.ref.markForCheck();
              this.ref.detectChanges();
            });
          }
        });
      }));
  }

  public handleSpaceRequest(page: ReportDesignerPageComponent, widget: WidgetDynamicBaseComponent, growthInfo: ReportDesignerWidgetGrowthInfo) {
    const widgetsOnPage = page.page.widgets;
    const numRowsNeeded = Math.round(growthInfo.additionalHeightNeeded / page.page.options.rowHeight);

    const otherWidgets = widgetsOnPage.filter(w => w.widgetId !== widget.widgetConfig.widgetId);

    const height = widget.widgetConfig.rows;
    const posY = widget.widgetConfig.y;
    const widgetEndRow = posY + height - 1;//Height minus 1

    //Valid: x, y, rows, cols
    if (otherWidgets.length) {
      if (growthInfo.moreColumnsRequestedIfOnPortraitPage) {
        //Scenario 5, not desired to be implemented! We just check for more rows here, too
      }

      //Widget isn't alone -> 
      //Check if there's more space below the widget
      const widgetsBelow = otherWidgets.filter(w => w.y > widgetEndRow);
      const widgetsAbove = otherWidgets.filter(w => w.y + w.rows < posY);

      let closestWidgetBelow: ReportDesignerWidgetConfig = null;
      let nearestYBelow = Number.POSITIVE_INFINITY;
      for (const widget of widgetsBelow) {
        if (widget.y < nearestYBelow) {
          nearestYBelow = widget.y;
          closestWidgetBelow = widget;
        }
      }

      let closestWidgetAbove: ReportDesignerWidgetConfig = null;
      let nearestYAbove = Number.POSITIVE_INFINITY;
      for (const widget of widgetsAbove) {
        if (widget.y < nearestYAbove) {
          nearestYAbove = widget.y;
          closestWidgetAbove = widget;
        }
      }

      let spaceBelowToNextWidget = page.page.options.maxRows - widgetEndRow - 1;
      if (closestWidgetBelow) {
        spaceBelowToNextWidget = closestWidgetBelow.y - widgetEndRow - 1;
      }

      let spaceAboveToNextWidget = widget.widgetConfig.y;
      if (closestWidgetAbove) {
        spaceAboveToNextWidget = widget.widgetConfig.y - closestWidgetAbove.y - closestWidgetAbove.rows;
      }

      if (spaceBelowToNextWidget >= numRowsNeeded) {
        this.expandBelow(widget, numRowsNeeded);
      }
      else if (spaceAboveToNextWidget >= numRowsNeeded) {
        this.expandAbove(widget, numRowsNeeded);
      }
      else if (spaceAboveToNextWidget + spaceBelowToNextWidget >= numRowsNeeded) {
        this.fillBetween(widget, numRowsNeeded, spaceAboveToNextWidget);
      }
      else {
        //Other widgets on the same page -> scenario 4 or 6
        //Widgets before the widget will stay as-is, the widget will be pushed onto new page(s) and then the other widgets follow onto the next page
        this.putWidgetToOwnPage(page, widget, widgetEndRow, growthInfo, posY);
      }
    }
    else {
      //Widget is alone
      if (page.page.options.isPortrait && growthInfo.moreColumnsRequestedIfOnPortraitPage) {
        //2: We just switch the page format and reload that page, so it will be checked again if it needs more rows. Landscape has different UI behavior

        this.switchPageOrientation(page);
      }
      else {
        if (widgetEndRow + numRowsNeeded < page.page.options.maxRows) {
          this.expandBelow(widget, numRowsNeeded);
        }
        else if (numRowsNeeded + height < page.page.options.maxRows) {
          this.expandAbove(widget, numRowsNeeded);
        }
        else {
          //The widget needs a virtual page
          let currentHeightOnPage = 0;
          const splitAtRows = [];
          for (let row = 1; row <= widget.getMaximumRowIndex(); ++row) {
            const heightForRow = growthInfo.rowSizes[row];
            if (isNaN(heightForRow)) {
              alert("Widget " + widget.name + ": Found a NAN sized row. Please check if the dynamic-growth-row are in a sequential order without gaps and that getMaximumRowIndex returns the correct index!")
            }
            currentHeightOnPage += heightForRow;

            //Is the next one too large for a page? If so, plit at current
            if ((currentHeightOnPage + heightForRow) > (page.page.options.maxRows * page.page.options.rowHeight)) {
              currentHeightOnPage = heightForRow;
              splitAtRows.push(row);
            }
          }

          splitAtRows.push(widget.getMaximumRowIndex());

          let lastPageStart = 0;
          const hashForWidget = widget.widgetDataHash;

          const currentPageClone = ObjectUtil.deepCopy(page.page);
          for (let i = 0; i < splitAtRows.length; ++i) {
            const pageCloneForWidget: ReportDesignerPage = ObjectUtil.deepCopy(currentPageClone);
            pageCloneForWidget.widgets = pageCloneForWidget.widgets.filter(w => w.widgetId === widget.widgetConfig.widgetId);
            pageCloneForWidget.widgets[0].y = 0;
            pageCloneForWidget.widgets[0].rows = pageCloneForWidget.options.maxRows;

            pageCloneForWidget.widgets[0].dataLoaded = false;
            pageCloneForWidget.widgets[0].widgetSettings.lowerRowIndex = lastPageStart;
            pageCloneForWidget.widgets[0].widgetSettings.upperRowIndex = splitAtRows[i];
            pageCloneForWidget.widgets[0].widgetSettings.id = widget.widgetConfig.widgetSettings.id + "::Overflow" + this.guidService.generate();
            pageCloneForWidget.widgets[0].widgetSettings.resizeDataHash = hashForWidget;

            pageCloneForWidget.dataLoaded = false;

            const pageId = 'OVERFLOW::' + this.guidService.generate();
            pageCloneForWidget.options.id = pageId;
            pageCloneForWidget.id = pageId;

            lastPageStart = splitAtRows[i] + 1;

            this.data.pages.splice(page.pageIndex + i + 1, 0, pageCloneForWidget);
          }
          this.data.pages.splice(page.pageIndex, 1);//Delete the current page

          for (let i = 0; i < this.data.pages.length; ++i) {
            this.data.pages[i].id = i.toString();
          }
          this.syncToPageList();
        }
      }
    }

    //TODO: Fake widget data loaded call if widget was removed from page
  }

  private putWidgetToOwnPage(page: ReportDesignerPageComponent, widget: WidgetDynamicBaseComponent, widgetEndRow: number, growthInfo: ReportDesignerWidgetGrowthInfo, posY: number) {
    const pageCloneAfter: ReportDesignerPage = ObjectUtil.deepCopy(page.page);
    pageCloneAfter.widgets = pageCloneAfter.widgets.filter(w => w.widgetId !== widget.widgetConfig.widgetId && w.y > widgetEndRow);

    for (let i = 0; i < pageCloneAfter.widgets.length; ++i) {
      pageCloneAfter.widgets[i].y -= (widgetEndRow + 1);
    }

    let currentHeightOnPage = 0;
    const splitAtRows = [];
    for (let row = 1; row <= widget.getMaximumRowIndex(); ++row) {
      const heightForRow = growthInfo.rowSizes[row];
      if (isNaN(heightForRow)) {
        alert("Widget " + widget.name + ": Found a NAN sized row. Please check if the dynamic-growth-row are in a sequential order without gaps and that getMaximumRowIndex returns the correct index!");
      }
      currentHeightOnPage += heightForRow;

      //Is the next one too large for a page? If so, plit at current
      if ((currentHeightOnPage + heightForRow) > (page.page.options.maxRows * page.page.options.rowHeight)) {
        currentHeightOnPage = heightForRow;
        splitAtRows.push(row);
      }
    }

    splitAtRows.push(widget.getMaximumRowIndex());

    let lastPageStart = 0;
    const hashForWidget = widget.widgetDataHash;

    const currentPageClone: ReportDesignerPage = ObjectUtil.deepCopy(page.page);
    for (let i = 0; i < splitAtRows.length; ++i) {
      const pageCloneForWidget: ReportDesignerPage = ObjectUtil.deepCopy(currentPageClone);
      pageCloneForWidget.widgets = pageCloneForWidget.widgets.filter(w => w.widgetId === widget.widgetConfig.widgetId);
      pageCloneForWidget.widgets[0].y = 0;
      pageCloneForWidget.widgets[0].rows = pageCloneForWidget.options.maxRows;

      pageCloneForWidget.widgets[0].dataLoaded = false;
      pageCloneForWidget.widgets[0].widgetSettings.lowerRowIndex = lastPageStart;
      pageCloneForWidget.widgets[0].widgetSettings.upperRowIndex = splitAtRows[i];
      pageCloneForWidget.widgets[0].widgetSettings.id = widget.widgetConfig.widgetSettings.id + "::Overflow" + this.guidService.generate();
      pageCloneForWidget.widgets[0].widgetSettings.resizeDataHash = hashForWidget;

      pageCloneForWidget.dataLoaded = false;

      const pageId = 'OVERFLOW::' + this.guidService.generate();
      pageCloneForWidget.options.id = pageId;
      pageCloneForWidget.id = pageId;

      lastPageStart = splitAtRows[i] + 1;

      if (i === 0) {
        //First page: Also include widgets next to the current widget (if appl.)
        const widgetsNextTo = currentPageClone.widgets.filter(w => w.widgetId !== widget.widgetConfig.widgetId && w.y + w.rows >= posY && w.y <= widgetEndRow);
        pageCloneForWidget.widgets.push(...widgetsNextTo);
      }

      this.data.pages.splice(page.pageIndex + i + 1, 0, pageCloneForWidget);
    }
    page.page.widgets = page.page.widgets.filter(w => w.widgetId !== widget.widgetConfig.widgetId && w.y + w.rows < posY);

    this.data.pages.splice(page.pageIndex + splitAtRows.length + 1, 0, pageCloneAfter);

    for (let i = 0; i < this.data.pages.length; ++i) {
      this.data.pages[i].id = i.toString();
    }
    this.syncToPageList();
  }

  private switchPageOrientation(page: ReportDesignerPageComponent) {
    const widgetClone: ReportDesignerWidgetConfig = ObjectUtil.deepCopy(page.page.widgets[0]);
    const pageIndex = page.pageIndex;
    this.data.pages.splice(page.pageIndex, 1);

    const newPage = this.addPage(this.widgetService.getHPageConfig(), pageIndex, false);
    newPage.options.id = 'OVERFLOW::' + this.guidService.generate();
    newPage.id = pageIndex.toString();

    widgetClone.x = 0;
    widgetClone.y = 0;

    widgetClone.cols = newPage.options.columns;
    widgetClone.rows = newPage.options.maxRows;
    widgetClone.minItemCols = newPage.options.columns;
    widgetClone.maxItemCols = newPage.options.columns;
    widgetClone.minItemRows = newPage.options.maxRows;
    widgetClone.maxItemRows = newPage.options.maxRows;

    widgetClone.sizeX = newPage.options.columns;
    widgetClone.sizeY = newPage.options.maxRows;

    newPage.widgets.push(widgetClone);
  }

  /**
   * The space between widgets can be taken!
   * @param widget
   * @param numRowsNeeded
   */
  private fillBetween(widget: WidgetDynamicBaseComponent, numRowsNeeded: number, spaceAboveToNextWidget: number) {
    widget.widgetConfig.y -= spaceAboveToNextWidget;
    widget.widgetConfig.rows += numRowsNeeded - spaceAboveToNextWidget + 1;
    widget.page.detectChanges();
  }

  /**
   * The space above can be taken!
   * @param widget
   * @param numRowsNeeded
   */
  private expandAbove(widget: WidgetDynamicBaseComponent, numRowsNeeded: number) {
    widget.widgetConfig.y -= numRowsNeeded;
    widget.widgetConfig.rows += numRowsNeeded;
    widget.page.detectChanges();
  }

  /**
   * The space below can be taken!
   * @param widget
   * @param numRowsNeeded
   */
  private expandBelow(widget: WidgetDynamicBaseComponent, numRowsNeeded: number) {
    widget.widgetConfig.rows += numRowsNeeded;
    widget.page.detectChanges();
  }
}
